import React, { useState } from 'react';
import { Progress, Tooltip, Button } from 'antd';
import {
    FileOutlined, FolderOutlined, CloseOutlined, CheckCircleFilled,
    CloseCircleFilled, DownOutlined, UpOutlined, DownloadOutlined,
} from '@ant-design/icons';
import { useDownloadManager, formatBytes } from '../../context/DownloadManagerContext';
import './DownloadTray.css';

const STATUS_LABEL = {
    downloading: 'Descargando…',
    done: 'Completado',
    error: 'Error',
    canceled: 'Cancelado',
};

const DownloadItem = ({ item, onCancel, onDismiss }) => {
    const isFolder = item.type === 'folder';
    const isIndeterminate = item.status === 'downloading' && !item.total;

    return (
        <div className={`dl-tray-item dl-tray-item--${item.status}`}>
            <div className="dl-tray-item__icon">
                {isFolder ? <FolderOutlined /> : <FileOutlined />}
            </div>
            <div className="dl-tray-item__body">
                <Tooltip title={item.name}>
                    <div className="dl-tray-item__name">{item.name}</div>
                </Tooltip>

                {item.status === 'downloading' && (
                    <>
                        {isIndeterminate ? (
                            <div className="dl-tray-item__indeterminate">
                                <div className="dl-tray-item__indeterminate-bar" />
                            </div>
                        ) : (
                            <Progress percent={item.percent ?? 0} size="small" showInfo={false} />
                        )}
                        <div className="dl-tray-item__meta">
                            {isFolder ? 'Comprimiendo y descargando… ' : ''}
                            {formatBytes(item.loaded)}
                            {item.total ? ` / ${formatBytes(item.total)}` : ''}
                            {item.rateLabel ? ` · ${item.rateLabel}` : ''}
                        </div>
                    </>
                )}

                {item.status === 'done' && (
                    <div className="dl-tray-item__meta dl-tray-item__meta--done">
                        <CheckCircleFilled /> Completado · {formatBytes(item.loaded)}
                    </div>
                )}

                {item.status === 'error' && (
                    <div className="dl-tray-item__meta dl-tray-item__meta--error">
                        <CloseCircleFilled /> {item.errorMessage}
                    </div>
                )}

                {item.status === 'canceled' && (
                    <div className="dl-tray-item__meta">Descarga cancelada</div>
                )}
            </div>
            <div className="dl-tray-item__action">
                {item.status === 'downloading' ? (
                    <Tooltip title="Cancelar descarga">
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => onCancel(item.id)}
                        />
                    </Tooltip>
                ) : (
                    <Tooltip title="Quitar de la lista">
                        <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => onDismiss(item.id)}
                        />
                    </Tooltip>
                )}
            </div>
        </div>
    );
};

/**
 * Bandeja flotante global de descargas (estilo Chrome/Drive). Se monta una
 * sola vez en la raíz de la app (ver App.js) para que las descargas sigan
 * visibles y en curso aunque el componente que las inició (p. ej. un Drawer
 * de FileManager) se cierre.
 */
const DownloadTray = () => {
    const { downloads, cancelDownload, removeDownload } = useDownloadManager();
    const [collapsed, setCollapsed] = useState(false);

    if (downloads.length === 0) return null;

    const activeCount = downloads.filter((d) => d.status === 'downloading').length;

    return (
        <div className="dl-tray">
            <div className="dl-tray__header" onClick={() => setCollapsed((c) => !c)}>
                <span className="dl-tray__title">
                    <DownloadOutlined /> Descargas{activeCount > 0 ? ` (${activeCount})` : ''}
                </span>
                {collapsed ? <UpOutlined /> : <DownOutlined />}
            </div>
            {!collapsed && (
                <div className="dl-tray__list">
                    {downloads.map((item) => (
                        <DownloadItem
                            key={item.id}
                            item={item}
                            onCancel={cancelDownload}
                            onDismiss={removeDownload}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default DownloadTray;
